import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  authService,
  deliveryCityService,
  deliveryNeighborhoodService,
  type DeliveryCity,
  type DeliveryNeighborhood,
  type ListDeliveryNeighborhoodsParams,
} from '../services';
import { useToast } from '../hooks/useToast';

interface NeighborhoodFormState {
  cityId: string;
  name: string;
  fee: string;
  isActive: boolean;
}

const EMPTY_FORM: NeighborhoodFormState = {
  cityId: '',
  name: '',
  fee: '0',
  isActive: true,
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const DeliveryNeighborhoodsPage: React.FC = () => {
  const [neighborhoods, setNeighborhoods] = useState<DeliveryNeighborhood[]>([]);
  const [cities, setCities] = useState<DeliveryCity[]>([]);
  const [loading, setLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [formState, setFormState] = useState<NeighborhoodFormState>(EMPTY_FORM);
  const [editingNeighborhood, setEditingNeighborhood] = useState<DeliveryNeighborhood | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [cnpjFilter, setCnpjFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeliveryNeighborhood | null>(null);

  const { showError, showSuccess, showInfo } = useToast();

  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';

  const loadCities = useCallback(
    async (targetCnpj?: string) => {
      try {
        setCitiesLoading(true);
        const data = await deliveryCityService.list({
          isActive: true,
          cnpj: isDeveloper ? targetCnpj || cnpjFilter.trim() || undefined : undefined,
        });
        setCities(data);
      } catch (error: any) {
        console.error('[DeliveryNeighborhoodsPage] loadCities error', error);
        showError(error.message || 'Não foi possível carregar as cidades.');
      } finally {
        setCitiesLoading(false);
      }
    },
    [cnpjFilter, isDeveloper, showError],
  );

  const loadNeighborhoods = useCallback(
    async (overrides?: Partial<ListDeliveryNeighborhoodsParams>) => {
      try {
        setLoading(true);
        const params: ListDeliveryNeighborhoodsParams = {
          cityId: cityFilter || undefined,
          searchTerm: searchTerm.trim() || undefined,
          isActive:
            statusFilter === 'all'
              ? undefined
              : statusFilter === 'active'
              ? true
              : false,
          ...(isDeveloper
            ? { cnpj: cnpjFilter.trim() || undefined }
            : {}),
          ...overrides,
        };

        const data = await deliveryNeighborhoodService.list(params);
        setNeighborhoods(data);
      } catch (error: any) {
        console.error('[DeliveryNeighborhoodsPage] loadNeighborhoods error', error);
        showError(error.message || 'Não foi possível carregar os bairros.');
      } finally {
        setLoading(false);
      }
    },
    [cityFilter, searchTerm, statusFilter, cnpjFilter, isDeveloper, showError],
  );

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  useEffect(() => {
    loadNeighborhoods();
  }, [loadNeighborhoods]);

  const handleOpenDialog = (neighborhood?: DeliveryNeighborhood) => {
    if (neighborhood) {
      setEditingNeighborhood(neighborhood);
      setFormState({
        cityId: neighborhood.cityId,
        name: neighborhood.name,
        fee: (neighborhood.fee ?? neighborhood.feeInCents / 100).toString(),
        isActive: neighborhood.isActive,
      });
    } else {
      setEditingNeighborhood(null);
      setFormState(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    setDialogOpen(false);
    setEditingNeighborhood(null);
    setFormState(EMPTY_FORM);
  };

  const parseFee = (value: string): number | null => {
    if (!value) return 0;
    const normalized = value.replace(',', '.');
    const feeNumber = Number(normalized);
    if (Number.isNaN(feeNumber) || feeNumber < 0) {
      return null;
    }
    return feeNumber;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.cityId) {
      showInfo('Selecione a cidade.');
      return;
    }
    if (!formState.name.trim()) {
      showInfo('Informe o nome do bairro.');
      return;
    }
    const parsedFee = parseFee(formState.fee);
    if (parsedFee === null) {
      showInfo('Informe uma taxa válida (ex: 7.90).');
      return;
    }

    try {
      setDialogLoading(true);
      if (editingNeighborhood) {
        const updated = await deliveryNeighborhoodService.update(editingNeighborhood.id, {
          name: formState.name,
          feeInCents: Math.round(parsedFee * 100),
          isActive: formState.isActive,
          cnpj: isDeveloper ? cnpjFilter.trim() || editingNeighborhood.cnpj : undefined,
        });
        showSuccess('Bairro atualizado com sucesso.');
        setNeighborhoods((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await deliveryNeighborhoodService.create({
          cityId: formState.cityId,
          name: formState.name,
          feeInCents: Math.round(parsedFee * 100),
          isActive: formState.isActive,
          cnpj: isDeveloper ? cnpjFilter.trim() || undefined : undefined,
        });
        showSuccess('Bairro cadastrado com sucesso.');
        setNeighborhoods((prev) => [created, ...prev]);
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('[DeliveryNeighborhoodsPage] handleSubmit error', error);
      showError(error.message || 'Não foi possível salvar o bairro.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deliveryNeighborhoodService.delete(
        deleteTarget.id,
        isDeveloper ? cnpjFilter.trim() || undefined : undefined,
      );
      setNeighborhoods((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      showSuccess('Bairro removido com sucesso.');
    } catch (error: any) {
      console.error('[DeliveryNeighborhoodsPage] handleDelete error', error);
      showError(error.message || 'Não foi possível remover o bairro.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtersActive = Boolean(
    searchTerm.trim() ||
      cityFilter ||
      statusFilter !== 'all' ||
      (isDeveloper && cnpjFilter.trim()),
  );

  const enrichedNeighborhoods = useMemo(() => {
    const cityById = new Map(cities.map((city) => [city.id, city] as const));
    return neighborhoods.map((item) => {
      const city = cityById.get(item.cityId);
      return {
        ...item,
        cityName: item.cityName || city?.name,
        cityState: item.cityState || city?.state,
      };
    });
  }, [cities, neighborhoods]);

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Bairros e taxas de entrega
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mt: 1 }}>
            Cadastre os bairros de cada cidade e configure a taxa de entrega em reais. Somente bairros ativos podem
            ser selecionados nos pedidos.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo bairro
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            label="Buscar por nome"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            fullWidth
            InputProps={{
              endAdornment: <SearchIcon fontSize="small" color="action" />,}}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="city-filter-label">Cidade</InputLabel>
            <Select
              labelId="city-filter-label"
              value={cityFilter}
              label="Cidade"
              onChange={(event) => setCityFilter(event.target.value)}
              disabled={citiesLoading}
            >
              <MenuItem value="">
                <em>Todas</em>
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name} ({city.state})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Ativos</MenuItem>
              <MenuItem value="inactive">Inativos</MenuItem>
            </Select>
          </FormControl>
          {isDeveloper && (
            <TextField
              label="CNPJ alvo"
              value={cnpjFilter}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, '').slice(0, 14);
                setCnpjFilter(digits);
                loadCities(digits);
              }}
              placeholder="Somente números"
              inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              sx={{ minWidth: 220 }}
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => loadNeighborhoods()}
            >
              Atualizar
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setCityFilter('');
                setStatusFilter('all');
                setCnpjFilter('');
                loadNeighborhoods({ searchTerm: undefined, cityId: undefined, isActive: undefined, cnpj: undefined });
                loadCities('');
              }}
              disabled={!filtersActive}
            >
              Limpar filtros
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{enrichedNeighborhoods.length} bairro(s)</Typography>
          {(loading || citiesLoading) && <CircularProgress size={22} />}
        </Box>
        <Divider />
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Bairro</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Taxa</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Atualização</TableCell>
                {isDeveloper && <TableCell>CNPJ</TableCell>}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrichedNeighborhoods.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={isDeveloper ? 7 : 6}>
                    <Box textAlign="center" py={5}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum bairro cadastrado com os filtros atuais.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {enrichedNeighborhoods.map((item) => (
                <TableRow hover key={item.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.cityName ? `${item.cityName} (${item.cityState})` : '—'}
                  </TableCell>
                  <TableCell>{currencyFormatter.format(item.fee)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={item.isActive ? 'success' : 'default'}
                      label={item.isActive ? 'Ativo' : 'Inativo'}
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(item.updatedAt), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  {isDeveloper && <TableCell>{item.cnpj || '—'}</TableCell>}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar">
                        <IconButton color="primary" onClick={() => handleOpenDialog(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton color="error" onClick={() => setDeleteTarget(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        component="form"
        onSubmit={handleSubmit}
      >
        <DialogTitle>{editingNeighborhood ? 'Editar bairro' : 'Novo bairro'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {isDeveloper && (
              <TextField
                label="CNPJ"
                value={cnpjFilter}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '').slice(0, 14);
                  setCnpjFilter(digits);
                  loadCities(digits);
                }}
                helperText="Somente números. Se vazio, será usado o CNPJ autenticado."
                inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
                fullWidth
              />
            )}
            <FormControl fullWidth required>
              <InputLabel id="city-select-label">Cidade</InputLabel>
              <Select
                labelId="city-select-label"
                value={formState.cityId}
                label="Cidade"
                onChange={(event) => setFormState((prev) => ({ ...prev, cityId: event.target.value }))}
                disabled={citiesLoading}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name} ({city.state})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Nome do bairro"
              value={formState.name}
              onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Taxa de entrega (R$)"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              value={formState.fee}
              onChange={(event) => setFormState((prev) => ({ ...prev, fee: event.target.value }))}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formState.isActive}
                  onChange={(event) => setFormState((prev) => ({ ...prev, isActive: event.target.checked }))}
                  color="primary"
                />
              }
              label="Bairro ativo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={dialogLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={dialogLoading}>
            {dialogLoading ? 'Salvando...' : editingNeighborhood ? 'Salvar alterações' : 'Cadastrar bairro'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover bairro</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Tem certeza de que deseja remover o bairro{' '}
            <Typography component="span" fontWeight={600}>
              {deleteTarget?.name}
            </Typography>
            ? Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeliveryNeighborhoodsPage;
